import { createFileRoute, useRouterState } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  FiActivity,
  FiBook,
  FiFilm,
  FiGrid,
  FiHeart,
  FiImage,
  FiMoreHorizontal,
  FiShoppingBag,
  FiShoppingCart,
  FiTruck,
  FiUsers,
} from 'react-icons/fi';
import { FaGavel } from 'react-icons/fa';
import { Card } from '@/components';
import { apiJson } from '@/lib/api';
import { formatMoneyAbsolute, formatShortDate } from '@/lib/utils';
import type {
  AdminHouseholdExpensesResponse,
  AdminUserDetailResponse,
  ExpenseCategoryListResponse,
  PaymentMethodListResponse,
  UnifiedItem,
} from '@/lib/types';

const categoryIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  general: FiGrid,
  school: FiBook,
  medical: FiHeart,
  clothing: FiShoppingBag,
  activities: FiActivity,
  groceries: FiShoppingCart,
  transportation: FiTruck,
  childcare: FiUsers,
  entertainment: FiFilm,
  other: FiMoreHorizontal,
};

function AdminUserExpensesPage() {
  const { userId } = Route.useParams();
  const id = Number(userId);
  const { getToken } = useAuth();
  const searchStr = useRouterState({ select: (s) => s.location.search });
  const hid = useMemo(() => new URLSearchParams(searchStr).get('hid') ?? '', [searchStr]);
  const hidNum = Number(hid);

  const userQuery = useQuery({
    queryKey: ['admin', 'user-detail', id],
    queryFn: () => apiJson<AdminUserDetailResponse>(getToken, `/v1/admin/users/${id}`),
  });

  const canView =
    !!hid &&
    Number.isFinite(hidNum) &&
    (userQuery.data?.households.some((h) => h.household_id === hidNum) ?? false);

  const bundleQuery = useQuery({
    queryKey: ['admin', 'household-expenses', hidNum, id],
    queryFn: () =>
      apiJson<AdminHouseholdExpensesResponse>(
        getToken,
        `/v1/admin/households/${hidNum}/expenses?perspective_user_id=${id}`,
      ),
    enabled: canView,
  });

  const categoriesQuery = useQuery({
    queryKey: ['expense-categories'],
    queryFn: () => apiJson<ExpenseCategoryListResponse>(getToken, '/v1/expense-categories'),
  });

  const paymentMethodsQuery = useQuery({
    queryKey: ['payment-methods'],
    queryFn: () => apiJson<PaymentMethodListResponse>(getToken, '/v1/payment-methods'),
  });

  const owedCents = bundleQuery.data?.accounting?.amount_owed_cents ?? 0;
  const expenses = bundleQuery.data?.expenses.expenses ?? [];
  const payments = bundleQuery.data?.payments.payments ?? [];
  const categories = categoriesQuery.data?.categories ?? [];
  const paymentMethods = paymentMethodsQuery.data?.payment_methods ?? [];

  const unifiedItems: UnifiedItem[] = useMemo(
    () =>
      [
        ...expenses.map((expense) => ({ type: 'expense' as const, data: expense })),
        ...payments.map((payment) => ({ type: 'payment' as const, data: payment })),
      ].sort((a, b) => {
        const dateA = a.type === 'expense' ? a.data.purchase_date : a.data.payment_date;
        const dateB = b.type === 'expense' ? b.data.purchase_date : b.data.payment_date;
        return new Date(dateB).getTime() - new Date(dateA).getTime();
      }),
    [expenses, payments],
  );

  const getCategory = (categoryId: string) => categories.find((c) => c.expense_category_id === categoryId);
  const getPaymentMethod = (methodId: string) =>
    paymentMethods.find((m) => m.payment_method_id === methodId);
  const getCategoryIconComponent = (categoryId: string) => categoryIcons[categoryId] || FiGrid;

  if (!hid || !canView) {
    return <p className='text-sm text-gray-500'>Select a valid household (use the household chips above).</p>;
  }

  const isLoading =
    bundleQuery.isLoading || categoriesQuery.isLoading || paymentMethodsQuery.isLoading;
  const isError = bundleQuery.isError || categoriesQuery.isError;

  return (
    <div>
      <h2 className='text-lg font-semibold text-gray-900 mb-4'>
        Expenses · household #{hidNum}
      </h2>

      <Card className='text-center mb-6'>
        <p className='text-sm font-semibold text-primary/80 mb-1'>Net balance (this user&apos;s perspective)</p>
        <p className='text-3xl font-bold text-gray-900'>{formatMoneyAbsolute(owedCents)}</p>
      </Card>

      <h3 className='text-base font-bold text-gray-900 mb-3'>Transactions</h3>

      {isLoading ? (
        <p className='text-gray-500 text-center py-8'>Loading…</p>
      ) : isError ? (
        <p className='text-gray-500 text-center py-8'>Couldn&apos;t load transactions.</p>
      ) : unifiedItems.length === 0 ? (
        <p className='text-gray-500 text-center py-8'>No transactions yet.</p>
      ) : (
        <div className='space-y-3'>
          {unifiedItems.map((item) => {
            if (item.type === 'expense') {
              const expense = item.data;
              const category = getCategory(expense.category);
              const categoryColor = category?.color ?? '#018B9C';
              const categoryName = category?.name ?? expense.category;
              const IconComponent = getCategoryIconComponent(expense.category);
              const hasDispute = (expense.disputes ?? []).length > 0;
              const hasAttachments = (expense.attachments ?? []).length > 0;

              const userSplit = expense.splits?.find((s) => s.split_for_user_id === id);
              let splitDisplay: string | undefined;
              if (expense.splits && expense.splits.length > 0 && userSplit) {
                const userPercent = userSplit.amount_percent;
                const otherPercent = expense.splits
                  .filter((s) => s.split_for_user_id !== id)
                  .reduce((sum, s) => sum + s.amount_percent, 0);
                splitDisplay = `${userPercent}% / ${otherPercent}%`;
              }

              return (
                <Card key={`expense-${expense.id}`} className='flex items-center gap-3'>
                  <div
                    className='w-10 h-10 rounded-full flex items-center justify-center relative shrink-0'
                    style={{ backgroundColor: categoryColor }}
                  >
                    <IconComponent className='w-5 h-5 text-white' />
                    {hasAttachments ? (
                      <div className='absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-primary flex items-center justify-center border-2 border-white'>
                        <FiImage className='w-2.5 h-2.5 text-white' />
                      </div>
                    ) : null}
                  </div>
                  <div className='flex-1 min-w-0'>
                    <p className='font-bold text-gray-900 truncate'>{expense.name}</p>
                    <div className='flex items-center gap-1.5 mt-1'>
                      <p className='text-xs text-gray-400'>
                        {formatShortDate(expense.purchase_date)} • {categoryName}
                      </p>
                      {hasDispute ? <FaGavel className='w-3 h-3 text-amber-500' /> : null}
                    </div>
                  </div>
                  <div className='text-right shrink-0'>
                    <p className='font-extrabold text-gray-900'>
                      {formatMoneyAbsolute(expense.amount_cents)}
                    </p>
                    {splitDisplay ? (
                      <p className='text-xs text-gray-400 mt-1'>{splitDisplay}</p>
                    ) : null}
                  </div>
                </Card>
              );
            }

            const payment = item.data;
            const method = getPaymentMethod(payment.payment_method);
            const methodColor = method?.color ?? '#9B59B6';
            const methodName = method?.name ?? payment.payment_method;
            const hasDispute = (payment.disputes ?? []).length > 0;

            return (
              <Card key={`payment-${payment.id}`} className='flex items-center gap-3'>
                <div
                  className='w-10 h-10 rounded-full flex items-center justify-center shrink-0'
                  style={{ backgroundColor: methodColor }}
                >
                  <span className='text-white font-bold text-lg'>$</span>
                </div>
                <div className='flex-1 min-w-0'>
                  <p className='font-bold text-gray-900'>Payment</p>
                  <div className='flex items-center gap-1.5 mt-1'>
                    <p className='text-xs text-gray-400'>
                      {formatShortDate(payment.payment_date)} • {methodName}
                    </p>
                    {hasDispute ? <FaGavel className='w-3 h-3 text-amber-500' /> : null}
                  </div>
                </div>
                <div className='text-right shrink-0'>
                  <p className='font-extrabold text-gray-900'>
                    {formatMoneyAbsolute(payment.amount_cents)}
                  </p>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute('/admin/users/$userId/expenses')({
  validateSearch: (search: Record<string, unknown>) => ({
    hid: typeof search.hid === 'string' ? search.hid : undefined,
  }),
  component: AdminUserExpensesPage,
});
